import type { AppLanguage } from "./index";

const localeByLanguage: Record<AppLanguage, string> = {
  en: "en-US",
  es: "es-PR",
};

export const getLocale = (language: AppLanguage): string =>
  localeByLanguage[language];

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateForDisplay = (value: Date | string | number): Date => {
  if (typeof value === "string") {
    const dateKey = value.match(DATE_KEY_PATTERN);
    if (dateKey) {
      const [, year, month, day] = dateKey;
      // A YYYY-MM-DD value is a calendar day, not a UTC instant. Construct it
      // in the viewer's local timezone so it never moves to the prior day.
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  return new Date(value);
};

export const formatCurrency = (
  amount: number,
  language: AppLanguage,
  currency = "USD"
): string =>
  new Intl.NumberFormat(getLocale(language), {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

export const formatDate = (
  value: Date | string | number,
  language: AppLanguage,
  options: Intl.DateTimeFormatOptions = {}
): string =>
  new Intl.DateTimeFormat(getLocale(language), options).format(
    parseDateForDisplay(value),
  );

export const formatDateTime = (
  value: Date | string | number,
  language: AppLanguage,
  options: Intl.DateTimeFormatOptions = {}
): string =>
  new Intl.DateTimeFormat(getLocale(language), {
    dateStyle: "medium",
    timeStyle: "short",
    ...options,
  }).format(parseDateForDisplay(value));
